import { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuPage } from '@/pages/MenuPage'
import { CheckoutPage } from '@/pages/CheckoutPage'
import { OrderTrackingPage } from '@/pages/OrderTrackingPage'
import { MenuDirectoryPage } from '@/pages/MenuDirectoryPage'
import { MenuNotFoundPage } from '@/pages/MenuNotFoundPage'
import { MenuUnavailablePage } from '@/pages/MenuUnavailablePage'
import { ActiveOrderBanner } from '@/components/checkout'
import { useMenuData } from '@/hooks/useMenuData'
import { useFavicon } from '@/hooks/useFavicon'
import { parseLangFromPath, parseMenuSlugFromPath, stripMenuSlugPrefix } from '@/utils/parseMenuSlug'

function App() {
  const { i18n } = useTranslation()
  const [route, setRoute] = useState<string>(window.location.pathname || '/')

  useEffect(() => {
    const onPopState = () => setRoute(window.location.pathname || '/')
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Extract language prefix and strip it from path for downstream parsing
  const { lang, pathWithoutLang } = useMemo(() => parseLangFromPath(route), [route])

  // Sync i18n language with URL lang prefix
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang, i18n])

  // First path segment (after lang) is the menu slug
  const menuSlug = useMemo(() => parseMenuSlugFromPath(pathWithoutLang), [pathWithoutLang])

  // When on a specific menu route, pass menu_slug so the backend only returns
  // that menu. On root (no slug) we need ALL active menus for smart-root logic.
  const {
    restaurant,
    menus,
    isLoading,
    isError,
    scheduleStatus,
    nextAvailableAt,
    unavailableMenuSlug,
  } = useMenuData({
    menuSlug: menuSlug || undefined,
  })
  useFavicon(restaurant?.logo_url)

  useEffect(() => {
    if (restaurant?.title) document.title = restaurant.title
  }, [restaurant?.title])

  // Sub-route within menu context: /summer-menu/checkout → '/checkout'
  const subRoute = useMemo(
    () => stripMenuSlugPrefix(pathWithoutLang, menuSlug),
    [pathWithoutLang, menuSlug],
  )

  // Build a full URL path with lang prefix (omit for English)
  const withLang = (path: string) => (lang === 'en' ? path : `/${lang}${path}`)

  // Navigate: auto-prepends menu slug for sub-routes
  const navigate = (path: string) => {
    const base = menuSlug && path.startsWith('/') ? `/${menuSlug}${path}` : path
    const fullPath = withLang(base)
    if (fullPath === route) return
    window.history.pushState({}, '', fullPath)
    setRoute(fullPath)
  }

  // Raw navigate: no slug prepend (used for SmartRoot redirects)
  const rawNavigate = (path: string) => {
    const fullPath = withLang(path)
    if (fullPath === route) return
    window.history.pushState({}, '', fullPath)
    setRoute(fullPath)
  }

  // Smart root: single-menu auto-redirect.
  // useLayoutEffect fires synchronously before the browser paints, so the user
  // never sees the RootLoadingScreen flash that a regular useEffect would cause.
  useLayoutEffect(() => {
    if (!menuSlug && !isLoading && menus.length === 1 && menus[0]?.slug) {
      rawNavigate(`/${menus[0].slug}`)
    }
  }, [menus, isLoading, menuSlug])

  if (!menuSlug && !isLoading && scheduleStatus === 'unavailable' && unavailableMenuSlug) {
    return (
      <MenuUnavailablePage
        slug={unavailableMenuSlug}
        nextAvailableAt={nextAvailableAt}
        businessTimeZone={restaurant?.timezone}
      />
    )
  }

  // Top-level tracking route: /tracking or /tracking/:code
  if (!menuSlug && pathWithoutLang.startsWith('/tracking')) {
    const trackingCode = pathWithoutLang.startsWith('/tracking/')
      ? pathWithoutLang.split('/tracking/')[1]
      : undefined
    return (
      <OrderTrackingPage
        navigate={rawNavigate}
        trackingCode={trackingCode}
      />
    )
  }

  // Smart root: / with no menu slug in path
  if (!menuSlug) {
    if (isLoading) return <RootLoadingScreen />
    if (menus.length === 0) return <NoMenusScreen />
    if (menus.length === 1) return null  // useLayoutEffect will redirect before paint
    return <MenuDirectoryPage menus={menus} onNavigate={rawNavigate} />
  }

  // Schedule-inactive menu: backend returns empty result with schedule_status='unavailable'.
  // Must be checked BEFORE not-found — empty menus array would otherwise trigger that page.
  if (menuSlug && !isLoading && scheduleStatus === 'unavailable') {
    return (
      <MenuUnavailablePage
        slug={menuSlug}
        nextAvailableAt={nextAvailableAt}
        businessTimeZone={restaurant?.timezone}
      />
    )
  }

  // Menu slug present but API returned an error (e.g. 404) — show not-found
  if (menuSlug && !isLoading && isError) {
    return <MenuNotFoundPage slug={menuSlug} />
  }

  // Validate slug once data is loaded
  const currentMenu = menus.find((m) => m.slug === menuSlug)
  if (!isLoading && menus.length > 0 && !currentMenu) {
    return <MenuNotFoundPage slug={menuSlug} />
  }

  const isCheckout = subRoute === '/checkout'
  const isTracking =
    subRoute === '/tracking' ||
    subRoute.startsWith('/tracking/') ||
    subRoute.startsWith('/order/')

  const renderPage = () => {
    if (isCheckout) return <CheckoutPage navigate={navigate} />
    if (isTracking) {
      const orderId = subRoute.startsWith('/order/')
        ? subRoute.split('/order/')[1]
        : undefined
      const trackingCode =
        subRoute.startsWith('/tracking/') && subRoute !== '/tracking'
          ? subRoute.split('/tracking/')[1]
          : undefined
      return (
        <OrderTrackingPage
          navigate={navigate}
          orderId={orderId}
          trackingCode={trackingCode}
        />
      )
    }
    // Both /menu and catch-all land on MenuPage (PreMenuPage bypassed)
    return <MenuPage onNavigate={navigate} />
  }

  return (
    <>
      {renderPage()}
      {!isCheckout && !isTracking && (
        <ActiveOrderBanner onViewOrder={() => navigate('/tracking')} />
      )}
    </>
  )
}

function RootLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
    </div>
  )
}

function NoMenusScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="text-center space-y-2 max-w-xs">
        <div className="text-4xl">🍽️</div>
        <h1 className="text-xl font-bold text-foreground">No menus available right now</h1>
        <p className="text-sm text-muted-foreground">
          This restaurant doesn&apos;t have any available menus right now.
        </p>
      </div>
    </div>
  )
}

export default App
